'use client';

import { useState } from 'react';

import type { OrderBookEntry } from '@/lib/hyperliquid/types';

interface OrderBookProps {
  symbol: string;
  midPrice: string;
  bids?: OrderBookEntry[];
  asks?: OrderBookEntry[];
  spread?: string;
  isLive?: boolean;
  onRowClick?: (price: string, size: string, side: 'Buy' | 'Sell') => void;
}

export default function OrderBook({ symbol, midPrice, bids = [], asks = [], spread = '0.00', isLive = false, onRowClick }: OrderBookProps) {
  const [view, setView] = useState<'both' | 'bids' | 'asks'>('both');
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  const base = symbol.split('/')[0];
  const displayMid = midPrice || '—';

  const handleRowClick = (price: string, size: string, side: 'Buy' | 'Sell', key: string) => {
    setHighlightedKey(key);
    onRowClick?.(price, size, side);
    // Clear highlight after 1.5s
    setTimeout(() => setHighlightedKey(prev => prev === key ? null : prev), 1500);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: '#141414' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#2a2a2a]" style={{ background: '#1e1e1e' }}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#F1F5F9]">Order Book</span>
          {isLive && (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-[#22C55E] animate-pulse" />
              <span className="text-[9px] text-[#22C55E] font-medium uppercase tracking-wider">Live</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {(['both', 'bids', 'asks'] as const).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-2 py-0.5 text-[10px] font-medium capitalize transition-all ${
                view === v ? 'bg-[rgba(76,110,244,0.2)] text-[#4c6ef4]' : 'text-[#94A3B8] hover:text-[#F1F5F9]'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-3 px-3 py-1.5 text-[10px] text-[#94A3B8] uppercase tracking-wider border-b border-[#2a2a2a]" style={{ background: '#1e1e1e' }}>
        <span>Price (USDC)</span>
        <span className="text-center">Size ({base})</span>
        <span className="text-right">Total</span>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {/* Asks */}
        {(view === 'both' || view === 'asks') && (
          <div className="flex-1 overflow-y-auto min-h-0 flex flex-col-reverse">
            {asks.length === 0 ? (
              <div className="flex items-center justify-center py-4">
                <span className="text-[10px] text-[#94A3B8]">Loading asks...</span>
              </div>
            ) : (
              asks.map((ask, i) => {
                const key = `ask-${i}-${ask.price}`;
                const isHighlighted = highlightedKey === key;
                return (
                  <div
                    key={key}
                    onClick={() => handleRowClick(ask.price, ask.size, 'Sell', key)}
                    className={`relative grid grid-cols-3 px-3 py-[3px] cursor-pointer group transition-colors duration-150 ${
                      isHighlighted ? 'bg-[rgba(76,110,244,0.25)] ring-1 ring-inset ring-[rgba(76,110,244,0.5)]' : 'hover:bg-[rgba(239,68,68,0.05)]'
                    }`}
                  >
                    <div className="absolute inset-y-0 right-0 bg-[rgba(239,68,68,0.08)]" style={{ width: `${ask.depth * 100}%` }} />
                    <span className="relative text-[#EF4444] text-xs font-mono tabular-nums">{ask.price}</span>
                    <span className="relative text-[#F1F5F9] text-xs font-mono tabular-nums text-center">{ask.size}</span>
                    <span className="relative text-[#94A3B8] text-xs font-mono tabular-nums text-right">{ask.total}</span>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Spread / Mid Price */}
        <div className="flex items-center justify-between px-3 py-1.5 border-y border-[#2a2a2a]" style={{ background: '#1e1e1e' }}>
          <span className="text-xs font-mono text-[#4c6ef4] font-semibold">${displayMid}</span>
          <span className="text-[10px] text-[#94A3B8]">Spread: <span className="text-[#F1F5F9]">${spread}</span></span>
        </div>

        {/* Bids */}
        {(view === 'both' || view === 'bids') && (
          <div className="flex-1 overflow-y-auto min-h-0">
            {bids.length === 0 ? (
              <div className="flex items-center justify-center py-4">
                <span className="text-[10px] text-[#94A3B8]">Loading bids...</span>
              </div>
            ) : (
              bids.map((bid, i) => {
                const key = `bid-${i}-${bid.price}`;
                const isHighlighted = highlightedKey === key;
                return (
                  <div
                    key={key}
                    onClick={() => handleRowClick(bid.price, bid.size, 'Buy', key)}
                    className={`relative grid grid-cols-3 px-3 py-[3px] cursor-pointer group transition-colors duration-150 ${
                      isHighlighted ? 'bg-[rgba(76,110,244,0.25)] ring-1 ring-inset ring-[rgba(76,110,244,0.5)]' : 'hover:bg-[rgba(34,197,94,0.05)]'
                    }`}
                  >
                    <div className="absolute inset-y-0 right-0 bg-[rgba(34,197,94,0.08)]" style={{ width: `${bid.depth * 100}%` }} />
                    <span className="relative text-[#22C55E] text-xs font-mono tabular-nums">{bid.price}</span>
                    <span className="relative text-[#F1F5F9] text-xs font-mono tabular-nums text-center">{bid.size}</span>
                    <span className="relative text-[#94A3B8] text-xs font-mono tabular-nums text-right">{bid.total}</span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}